import { z } from 'zod';
import {
  isoDateSchema,
  longTextSchema,
  moneySchema,
  nonNegativeMoneySchema,
  positiveMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import {
  BIKE_OWNERSHIPS,
  BIKE_STATUSES,
  HIRE_PURCHASE_STATUSES,
  INSTALMENT_FREQUENCIES,
  INSTALMENT_FREQUENCY_DAYS,
  REMITTANCE_FREQUENCIES,
  REMITTANCE_STATES,
  VEHICLE_CATEGORIES,
  type InstalmentFrequency,
  type RemittanceState,
} from '../enums.js';

/**
 * Remittance & Hire Purchase.
 *
 * Three screens over one register:
 *
 *   Bikes          the asset, for its whole life
 *   Remittance     what each rider owes for a week, and what has come in
 *   Hire purchase  a bike being bought back, instalment by instalment
 *
 * The one rule that shapes everything here: **a hire purchase repayment is not
 * remittance revenue.** The weekly remittance is earned when the week ends —
 * the company put the rider on the roster. A repayment is the rider settling a
 * receivable created when the bike was sold to them, and counting it as income
 * would book the same bike twice. The two never share a total, and
 * `agreementProgress` below returns `outstanding`, never `revenue`.
 */

// ── Bikes ───────────────────────────────────────────────────────────────────

/**
 * A plate, normalised the way the unique index normalises it.
 *
 * `lower(btrim(registration))` in the database, trimmed and upper-cased here,
 * so "ch 204 abc " and "CH 204 ABC" are the same bike before anybody has a
 * chance to create the second one.
 */
export const registrationSchema = z
  .string()
  .trim()
  .min(2, 'A registration needs at least two characters')
  .max(40)
  .transform((v) => v.replace(/\s+/g, ' ').toUpperCase());

export const bikeInputSchema = z
  .object({
    registration: registrationSchema,
    /**
     * What kind of vehicle it is.
     *
     * Defaults to motorcycle so that every caller written before the fleet
     * existed — the register form, the importer, the tests — keeps creating
     * what it always created.
     */
    category: z.enum(VEHICLE_CATEGORIES).default('motorcycle'),
    make: shortTextSchema.optional(),
    model: shortTextSchema.optional(),
    seats: z.number().int().min(1).max(100).optional(),
    capacityKg: nonNegativeMoneySchema.optional(),
    /**
     * What this vehicle is authorised for.
     *
     * Empty is not "anything": the delivery form falls back to the category's
     * own rule, which is the conservative reading. Nothing here enables
     * passenger work on its own — that needs the module too.
     */
    services: z.array(shortTextSchema).max(20).optional(),
    purchaseDate: isoDateSchema.optional(),
    acquisitionCost: nonNegativeMoneySchema.optional(),
    estimatedValue: nonNegativeMoneySchema.optional(),
    /**
     * Compulsory, and deliberately not defaulted: who owns a bike decides who
     * its remittance belongs to, so nobody gets to skip the question.
     */
    ownership: z.enum(BIKE_OWNERSHIPS, {
      required_error: 'Choose who owns this bike: Company, Investor or Rider',
      invalid_type_error: 'Choose who owns this bike: Company, Investor or Rider',
    }),
    /** The investor, from the investor register, when an investor owns it. */
    investorId: uuidSchema.nullish(),
    /**
     * The investor's weekly share of this bike's remittance, when it is not the
     * business default. Recorded as the bike's share agreement.
     */
    investorWeeklyAmount: nonNegativeMoneySchema.optional(),
    ownerName: shortTextSchema.optional(),
    branchId: uuidSchema.nullish(),
    riderId: uuidSchema.nullish(),
    gpsImei: z
      .string()
      .trim()
      .regex(/^[0-9]{14,17}$/, 'An IMEI is 14 to 17 digits')
      .optional(),
    status: z.enum(BIKE_STATUSES).default('active'),
    note: longTextSchema.optional(),
  })
  /*
    An investor's bike with no investor named is a record that cannot answer
    the only question it exists to answer. Checked here rather than in the
    database, because "other" is legitimately unnamed — a bike the business
    inherited with the premises has an ownership and no owner.
  */
  .refine((v) => v.ownership !== 'investor' || Boolean(v.investorId), {
    path: ['investorId'],
    message: 'Choose the investor who owns it',
  })
  .refine((v) => v.ownership === 'investor' || v.investorWeeklyAmount === undefined, {
    path: ['investorWeeklyAmount'],
    message: 'Only an investor-owned bike has an investor share',
  });
export type BikeInput = z.infer<typeof bikeInputSchema>;

export const bikeUpdateSchema = bikeInputSchema.innerType().innerType().partial();
export type BikeUpdate = z.infer<typeof bikeUpdateSchema>;

/** Hand a bike to a rider, or take it back. */
export const bikeAssignmentInputSchema = z.object({
  riderId: uuidSchema,
  assignedOn: isoDateSchema,
  note: longTextSchema.optional(),
});
export type BikeAssignmentInput = z.infer<typeof bikeAssignmentInputSchema>;

export const bikeReleaseSchema = z.object({
  releasedOn: isoDateSchema,
  note: longTextSchema.optional(),
});

export const bikeFiltersSchema = z.object({
  category: z.enum(VEHICLE_CATEGORIES).optional(),
  status: z.enum(BIKE_STATUSES).optional(),
  ownership: z.enum(BIKE_OWNERSHIPS).optional(),
  branchId: uuidSchema.optional(),
  riderId: uuidSchema.optional(),
  /** Matches a registration or a model, case-insensitively. */
  search: z.string().trim().max(80).optional(),
});
export type BikeFilters = z.infer<typeof bikeFiltersSchema>;

export const bikeAssignmentViewSchema = z.object({
  id: uuidSchema,
  riderId: uuidSchema,
  riderName: z.string(),
  assignedOn: z.string(),
  releasedOn: z.string().nullable(),
  note: z.string().nullable(),
});
export type BikeAssignmentView = z.infer<typeof bikeAssignmentViewSchema>;

export const bikeViewSchema = z.object({
  id: uuidSchema,
  registration: z.string(),
  category: z.enum(VEHICLE_CATEGORIES),
  make: z.string().nullable(),
  model: z.string().nullable(),
  seats: z.number().int().nullable(),
  capacityKg: moneySchema.nullable(),
  services: z.array(z.string()),
  purchaseDate: z.string().nullable(),
  acquisitionCost: moneySchema.nullable(),
  estimatedValue: moneySchema.nullable(),
  ownership: z.enum(BIKE_OWNERSHIPS),
  ownerName: z.string().nullable(),
  investorId: uuidSchema.nullable(),
  investorName: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  riderId: uuidSchema.nullable(),
  riderName: z.string().nullable(),
  gpsImei: z.string().nullable(),
  status: z.enum(BIKE_STATUSES),
  note: z.string().nullable(),
  /** The live agreement on this bike, when there is one. At most one. */
  agreementId: uuidSchema.nullable(),
  createdAt: z.string(),
});
export type BikeView = z.infer<typeof bikeViewSchema>;

export const bikeOwnershipPeriodViewSchema = z.object({
  id: uuidSchema,
  ownership: z.enum(BIKE_OWNERSHIPS),
  investorId: uuidSchema.nullable(),
  investorName: z.string().nullable(),
  ownerName: z.string().nullable(),
  /** In force from this date until the next period's. */
  effectiveFrom: z.string(),
  reason: z.string(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type BikeOwnershipPeriodView = z.infer<typeof bikeOwnershipPeriodViewSchema>;

export const investorEntitlementViewSchema = z.object({
  id: uuidSchema,
  weeklyAmount: moneySchema,
  /** Set when the agreement is a percentage of each week's remittance (0147). */
  percent: z.string().nullable().optional(),
  effectiveFrom: z.string(),
  reason: z.string(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type InvestorEntitlementView = z.infer<typeof investorEntitlementViewSchema>;

export const bikeDetailSchema = bikeViewSchema.extend({
  assignments: z.array(bikeAssignmentViewSchema),
  ownershipHistory: z.array(bikeOwnershipPeriodViewSchema),
  /** Agreements for this bike's investor share, newest first. */
  entitlements: z.array(investorEntitlementViewSchema),
  /** What an investor is entitled to each week now: this bike's agreement, else the business default. */
  currentEntitlement: z.object({
    weeklyAmount: moneySchema,
    percent: z.string().nullable().optional(),
    source: z.enum(['agreement', 'default']),
  }),
});
export type BikeDetail = z.infer<typeof bikeDetailSchema>;

export const bikeRegisterSchema = z.object({
  bikes: z.array(bikeViewSchema),
  totals: z.object({
    count: z.number().int(),
    inService: z.number().int(),
    onHirePurchase: z.number().int(),
    acquisitionCost: moneySchema,
    estimatedValue: moneySchema,
  }),
});
export type BikeRegister = z.infer<typeof bikeRegisterSchema>;

// ── What a kind of vehicle owes ─────────────────────────────────────────────

/**
 * Setting a default. The date is required and deliberately not defaulted.
 *
 * A fee change is effective-dated, and "from when" is the question that
 * decides which weeks it governs. Defaulting it to today would quietly pick an
 * answer on behalf of somebody agreeing a price that starts next month.
 */
export const vehicleRateInputSchema = z.object({
  category: z.enum(VEHICLE_CATEGORIES),
  /** Null, or absent, prices the whole business rather than one state. */
  stateCode: z.string().trim().min(2).max(8).nullish(),
  frequency: z.enum(REMITTANCE_FREQUENCIES),
  amount: nonNegativeMoneySchema,
  effectiveFrom: isoDateSchema,
  note: longTextSchema.optional(),
});
export type VehicleRateInput = z.infer<typeof vehicleRateInputSchema>;

export const vehicleRateViewSchema = z.object({
  id: uuidSchema,
  category: z.enum(VEHICLE_CATEGORIES),
  stateCode: z.string().nullable(),
  frequency: z.enum(REMITTANCE_FREQUENCIES),
  amount: moneySchema,
  effectiveFrom: z.string(),
  /** Null means still in force. A closed row is history, kept readable. */
  effectiveTo: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type VehicleRateView = z.infer<typeof vehicleRateViewSchema>;

// ── Remittance ──────────────────────────────────────────────────────────────

export const remittancePaymentInputSchema = z.object({
  amount: positiveMoneySchema,
  paidOn: isoDateSchema,
  method: shortTextSchema.optional(),
  note: longTextSchema.optional(),
  /** The cash or bank account it was paid into, when no default account would receive it. */
  accountId: uuidSchema.optional(),
});
export type RemittancePaymentInput = z.infer<typeof remittancePaymentInputSchema>;

export const remittanceWaiverSchema = z.object({
  /** Required. A waiver without a stated reason is unauditable. */
  reason: shortTextSchema,
});

export const remittancePaymentViewSchema = z.object({
  id: uuidSchema,
  amount: moneySchema,
  paidOn: z.string(),
  method: z.string().nullable(),
  note: z.string().nullable(),
  recordedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type RemittancePaymentView = z.infer<typeof remittancePaymentViewSchema>;

export const remittanceRowSchema = z.object({
  id: uuidSchema,
  riderId: uuidSchema,
  riderName: z.string(),
  riderPhone: z.string().nullable(),
  bikeId: uuidSchema.nullable(),
  bikeRegistration: z.string().nullable(),
  /** The vehicle's category, where the week records a vehicle. */
  vehicleCategory: z.enum(VEHICLE_CATEGORIES).nullable(),
  /**
   * What the configured default says this driver owes TODAY.
   *
   * Shown beside `amountAccrued`, which is what the week actually charged.
   * The two differ whenever a default has moved since the week was accrued,
   * and seeing both is how somebody tells "the price changed in March" from
   * "this week was charged wrongly" — only the second is worth correcting.
   */
  defaultRemittance: moneySchema.nullable(),
  rides: z.number().int(),
  /** What the week charged. Revenue, recognised when the week ended. */
  amountAccrued: moneySchema,
  amountPaid: moneySchema,
  outstanding: moneySchema,
  dueOn: z.string(),
  state: z.enum(REMITTANCE_STATES),
  /** Negative until the due date, positive once it has passed. */
  daysLate: z.number().int(),
  waivedReason: z.string().nullable(),
  payments: z.array(remittancePaymentViewSchema),
});
export type RemittanceRow = z.infer<typeof remittanceRowSchema>;

export const remittancePeriodViewSchema = z.object({
  id: uuidSchema,
  label: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  /** When the charge is earned. Falls back to the period end. */
  revenueRecognitionDate: z.string(),
  /** Period end plus the business grace period. */
  dueOn: z.string(),
  accruedAt: z.string().nullable(),
});
export type RemittancePeriodView = z.infer<typeof remittancePeriodViewSchema>;

export const remittanceBoardSchema = z.object({
  periods: z.array(remittancePeriodViewSchema),
  /** The period the rows belong to. Null when no period exists yet. */
  period: remittancePeriodViewSchema.nullable(),
  graceDays: z.number().int(),
  rows: z.array(remittanceRowSchema),
  totals: z.object({
    riders: z.number().int(),
    /** Accrued excluding waived rows — the revenue figure, unchanged by this feature. */
    accrued: moneySchema,
    collected: moneySchema,
    outstanding: moneySchema,
    waived: moneySchema,
    overdueCount: z.number().int(),
  }),
});
export type RemittanceBoard = z.infer<typeof remittanceBoardSchema>;

/**
 * Where a week's remittance stands, from the numbers and the date.
 *
 * Shared so the screen, the API and the tests cannot disagree. Pure, and
 * `today` is passed in rather than read from the clock — a function that reads
 * the clock cannot be tested at the boundary, and the boundary is the whole
 * behaviour.
 *
 * `settled` carries the legacy weeks: rows marked paid before the payment
 * ledger existed have no payment rows, and deriving them from an empty ledger
 * would restate settled history as overdue.
 */
export function remittanceState(input: {
  accrued: number;
  paid: number;
  dueOn: string;
  today: string;
  waived: boolean;
  settled: boolean;
}): RemittanceState {
  if (input.waived) return 'waived';
  if (input.settled || input.paid >= input.accrued) return 'paid';
  if (input.paid > 0) return input.today > input.dueOn ? 'overdue' : 'partially_paid';
  return input.today > input.dueOn ? 'overdue' : 'due';
}

// ── Hire purchase ───────────────────────────────────────────────────────────

const hirePurchaseFields = z.object({
  riderId: uuidSchema,
  /** The register's row. There is no way to write an agreement without one. */
  bikeId: uuidSchema,
  branchId: uuidSchema.nullish(),
  bikeCost: nonNegativeMoneySchema,
  sellingPrice: nonNegativeMoneySchema,
  deposit: nonNegativeMoneySchema.default('0'),
  instalmentAmount: positiveMoneySchema,
  instalmentFrequency: z.enum(INSTALMENT_FREQUENCIES).default('weekly'),
  startsOn: isoDateSchema,
  note: longTextSchema.optional(),
});

export const hirePurchaseInputSchema = hirePurchaseFields
  .refine((v) => Number(v.deposit) <= Number(v.sellingPrice), {
    path: ['deposit'],
    message: 'A deposit cannot exceed the selling price',
  })
  /*
    An instalment larger than what is left to pay is not an error — a single
    final payment clears the balance. An instalment larger than the whole
    financed amount means somebody typed the price into the instalment field,
    which produces an agreement that completes before it starts.
  */
  .refine(
    (v) =>
      Number(v.sellingPrice) - Number(v.deposit) === 0 ||
      Number(v.instalmentAmount) <= Number(v.sellingPrice) - Number(v.deposit),
    {
      path: ['instalmentAmount'],
      message: 'An instalment cannot exceed the amount being financed',
    },
  );
export type HirePurchaseInput = z.infer<typeof hirePurchaseInputSchema>;

export const hirePurchaseUpdateSchema = hirePurchaseFields
  .pick({
    branchId: true,
    sellingPrice: true,
    instalmentAmount: true,
    instalmentFrequency: true,
    note: true,
  })
  .partial()
  .extend({ status: z.enum(HIRE_PURCHASE_STATUSES).optional() });
export type HirePurchaseUpdate = z.infer<typeof hirePurchaseUpdateSchema>;

export const hirePurchasePaymentInputSchema = z.object({
  amount: positiveMoneySchema,
  paidOn: isoDateSchema,
  method: shortTextSchema.optional(),
  note: longTextSchema.optional(),
});
export type HirePurchasePaymentInput = z.infer<typeof hirePurchasePaymentInputSchema>;

export const hirePurchasePaymentViewSchema = remittancePaymentViewSchema;
export type HirePurchasePaymentView = z.infer<typeof hirePurchasePaymentViewSchema>;

export const hirePurchaseViewSchema = z.object({
  id: uuidSchema,
  riderId: uuidSchema,
  riderName: z.string(),
  bikeId: uuidSchema,
  bikeRegistration: z.string(),
  bikeModel: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),

  bikeCost: moneySchema,
  sellingPrice: moneySchema,
  deposit: moneySchema,
  /** `sellingPrice - deposit`. Derived, never stored. */
  financed: moneySchema,
  /** `sellingPrice - bikeCost`. The margin on the sale, not on the repayments. */
  margin: moneySchema,

  instalmentAmount: moneySchema,
  instalmentFrequency: z.enum(INSTALMENT_FREQUENCIES),
  startsOn: z.string(),
  expectedCompletionOn: z.string().nullable(),
  status: z.enum(HIRE_PURCHASE_STATUSES),
  note: z.string().nullable(),

  /** Deposit plus every instalment received. A receivable, never revenue. */
  amountPaid: moneySchema,
  outstanding: moneySchema,
  instalmentsPaid: z.number().int(),
  instalmentsExpected: z.number().int(),
  /** Instalments the schedule says should have been received by now, minus those that were. */
  instalmentsBehind: z.number().int(),
  /** 0–100, rounded. Deposit counts towards it, because the rider paid it. */
  percentPaid: z.number(),
  payments: z.array(hirePurchasePaymentViewSchema),
});
export type HirePurchaseView = z.infer<typeof hirePurchaseViewSchema>;

export const hirePurchaseBoardSchema = z.object({
  agreements: z.array(hirePurchaseViewSchema),
  totals: z.object({
    agreements: z.number().int(),
    live: z.number().int(),
    financed: moneySchema,
    collected: moneySchema,
    /** What riders still owe on bikes. An asset, and it is not revenue. */
    outstanding: moneySchema,
    behindCount: z.number().int(),
  }),
});
export type HirePurchaseBoard = z.infer<typeof hirePurchaseBoardSchema>;

export const hirePurchaseFiltersSchema = z.object({
  status: z.enum(HIRE_PURCHASE_STATUSES).optional(),
  riderId: uuidSchema.optional(),
  bikeId: uuidSchema.optional(),
  branchId: uuidSchema.optional(),
});
export type HirePurchaseFilters = z.infer<typeof hirePurchaseFiltersSchema>;

/**
 * How far along an agreement is, and whether the rider is behind.
 *
 * Pure, `today` passed in, and shared with the screen for the same reason
 * `remittanceState` is: a progress bar that computes its own arithmetic is a
 * second implementation of the receivable.
 *
 * Returns `outstanding`. There is no `revenue` here and there must never be
 * one — see the note at the top of this file.
 */
export function agreementProgress(input: {
  sellingPrice: number;
  deposit: number;
  instalmentAmount: number;
  instalmentFrequency: InstalmentFrequency;
  startsOn: string;
  paymentsTotal: number;
  today: string;
}): {
  financed: number;
  amountPaid: number;
  outstanding: number;
  instalmentsPaid: number;
  instalmentsExpected: number;
  instalmentsBehind: number;
  percentPaid: number;
  expectedCompletionOn: string;
} {
  const financed = Math.max(0, input.sellingPrice - input.deposit);
  // The deposit is money the rider paid, so it counts towards the price.
  const amountPaid = Math.min(input.sellingPrice, input.deposit + input.paymentsTotal);
  const outstanding = Math.max(0, input.sellingPrice - amountPaid);

  const perDays = INSTALMENT_FREQUENCY_DAYS[input.instalmentFrequency];
  const instalmentsExpected = Math.ceil(financed / input.instalmentAmount);
  const instalmentsPaid = Math.floor(input.paymentsTotal / input.instalmentAmount);

  /*
    Instalments the schedule says should have arrived by now.

    Counted in whole periods elapsed since the start date, capped at the
    schedule's length — an agreement that ran past its projected end is behind
    by what it still owes, not by every week since.
  */
  const elapsed = Math.max(0, Math.floor(daysBetween(input.startsOn, input.today) / perDays));
  const instalmentsDue = Math.min(instalmentsExpected, elapsed);
  const instalmentsBehind = Math.max(0, instalmentsDue - instalmentsPaid);

  return {
    financed,
    amountPaid,
    outstanding,
    instalmentsPaid,
    instalmentsExpected,
    instalmentsBehind,
    percentPaid:
      input.sellingPrice === 0 ? 100 : Math.round((amountPaid / input.sellingPrice) * 100),
    expectedCompletionOn: addDays(input.startsOn, instalmentsExpected * perDays),
  };
}

/** Whole days from one ISO date to another. Both are calendar dates, so UTC is exact. */
function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/** An ISO date, `days` later. Used for projections, never for "now". */
export function addDays(from: string, days: number): string {
  const at = new Date(Date.parse(`${from}T00:00:00Z`) + days * 86_400_000);
  return at.toISOString().slice(0, 10);
}
